import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, IndianRupee } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const fmtINRShort = (n) => {
  const v = Math.round(n || 0);
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (v >= 1_00_000) return `₹${(v / 1_00_000).toFixed(2)} L`;
  if (v >= 1_000) return `₹${(v / 1_000).toFixed(1)}K`;
  return `₹${v}`;
};

function calcEMI(principal, annualRatePct, years) {
  const n = Math.max(1, Math.round(years * 12));
  const r = annualRatePct / 12 / 100;
  if (r === 0) return { emi: principal / n, totalInterest: 0, totalPayable: principal };
  const pow = Math.pow(1 + r, n);
  const emi = (principal * r * pow) / (pow - 1);
  const totalPayable = emi * n;
  return { emi, totalInterest: totalPayable - principal, totalPayable };
}

export default function EMICalculator({ defaultLoanType = "home_loan" }) {
  const [amount, setAmount] = useState(50_00_000); // 50 L
  const [rate, setRate] = useState(8.75);
  const [years, setYears] = useState(20);

  const { emi, totalInterest, totalPayable } = useMemo(
    () => calcEMI(amount, rate, years),
    [amount, rate, years]
  );

  const interestShare = totalPayable === 0 ? 0 : (totalInterest / totalPayable) * 100;

  return (
    <div data-testid="emi-calculator" className="card-soft p-6 md:p-8">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-forest text-brand-gold">
          <Calculator className="h-5 w-5" />
        </span>
        <div>
          <div className="eyebrow !text-brand-terracotta">Plan Your Loan</div>
          <h3 className="font-outfit font-bold text-2xl text-brand-forest">EMI Calculator</h3>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-8 lg:gap-12 items-start">
        {/* Inputs */}
        <div className="space-y-7">
          <SliderField
            testid="emi-amount"
            label="Loan Amount"
            value={amount}
            display={fmtINRShort(amount)}
            min={5_00_000}
            max={10_00_00_000}
            step={50_000}
            onChange={setAmount}
            inputAdornment={<IndianRupee className="h-4 w-4 text-brand-sage" />}
          />
          <SliderField
            testid="emi-rate"
            label="Interest Rate"
            value={rate}
            display={`${rate.toFixed(2)}%`}
            min={6}
            max={18}
            step={0.05}
            onChange={(v) => setRate(Number(v.toFixed(2)))}
            decimal
          />
          <SliderField
            testid="emi-years"
            label="Tenure"
            value={years}
            display={`${years} ${years === 1 ? "year" : "years"}`}
            min={1}
            max={30}
            step={1}
            onChange={setYears}
          />
        </div>

        {/* Output */}
        <div className="rounded-2xl bg-brand-forest text-brand-cream p-7 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-brand-terracotta/25 blur-2xl" />
          <div className="absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-brand-gold/20 blur-2xl" />

          <div className="relative">
            <div className="text-xs uppercase tracking-[0.18em] text-brand-gold">Monthly EMI</div>
            <div data-testid="emi-result-monthly" className="mt-2 font-outfit font-extrabold text-4xl sm:text-5xl tracking-tight">
              ₹{fmtINR(emi)}
            </div>

            <div className="mt-7 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-brand-cream/60 text-xs uppercase tracking-[0.14em]">Principal</div>
                <div data-testid="emi-result-principal" className="mt-1 font-outfit font-semibold text-lg text-brand-cream">
                  {fmtINRShort(amount)}
                </div>
              </div>
              <div>
                <div className="text-brand-cream/60 text-xs uppercase tracking-[0.14em]">Total Interest</div>
                <div data-testid="emi-result-interest" className="mt-1 font-outfit font-semibold text-lg text-brand-gold">
                  {fmtINRShort(totalInterest)}
                </div>
              </div>
              <div className="col-span-2 pt-4 border-t border-brand-cream/15">
                <div className="text-brand-cream/60 text-xs uppercase tracking-[0.14em]">Total Payable</div>
                <div data-testid="emi-result-total" className="mt-1 font-outfit font-semibold text-xl text-brand-cream">
                  ₹{fmtINR(totalPayable)}
                </div>
              </div>
            </div>

            {/* Interest share bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-brand-cream/60">
                <span>Interest share</span>
                <span>{interestShare.toFixed(1)}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-brand-cream/15 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-brand-gold to-brand-terracotta transition-all duration-500"
                  style={{ width: `${Math.min(100, interestShare)}%` }}
                />
              </div>
            </div>

            <Link
              data-testid="emi-apply-btn"
              to={`/apply?type=${defaultLoanType}&amount=${amount}`}
              className="mt-7 inline-flex items-center justify-center gap-2 w-full rounded-full bg-brand-terracotta px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#b25535] hover:-translate-y-0.5"
            >
              Apply with these numbers <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-3 text-[11px] text-brand-cream/55 text-center">
              Indicative only. Final EMI depends on lender approval, profile and processing fees.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderField({ testid, label, value, display, min, max, step, onChange, inputAdornment, decimal }) {
  const handleInput = (e) => {
    const raw = e.target.value.replace(/[^\d.]/g, "");
    const v = decimal ? parseFloat(raw) : parseInt(raw, 10);
    if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <Label className="text-brand-forest font-medium text-sm">{label}</Label>
        <div className="relative">
          {inputAdornment && (
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2">{inputAdornment}</span>
          )}
          <Input
            value={display.replace(/^₹\s?/, "").replace(/\s(L|Cr|K|years|year)$/, "")}
            onChange={handleInput}
            className={`h-9 w-32 text-right font-outfit font-semibold text-brand-forest ${
              inputAdornment ? "pl-7" : ""
            }`}
            data-testid={`${testid}-input`}
          />
        </div>
      </div>
      <Slider
        className="mt-4"
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={`${testid}-slider`}
      />
      <div className="mt-2 flex items-center justify-between text-[11px] text-brand-sage">
        <span>{typeof min === "number" && !decimal ? fmtINRShort(min) : decimal ? `${min}%` : min}</span>
        <span className="font-semibold text-brand-terracotta">{display}</span>
        <span>{typeof max === "number" && !decimal ? fmtINRShort(max) : decimal ? `${max}%` : max}</span>
      </div>
    </div>
  );
}
